import React from 'react';
import { Avatar, AvatarGroup, HStack, Text } from '@chakra-ui/react';
import { FriendActivity } from '@/types/restaurant';

interface FriendAvatarsProps {
  friends: FriendActivity[];
  maxAvatars?: number;
  size?: 'xs' | 'sm' | 'md';
}

function getActionVerb(friends: FriendActivity[]): string {
  const ratedCount = friends.filter(f => f.action === 'rated').length;
  const bookmarkedCount = friends.filter(f => f.action === 'bookmarked').length;

  if (ratedCount > 0 && bookmarkedCount > 0) {
    return 'rated or saved';
  } else if (ratedCount > 0) {
    return 'rated';
  } else if (bookmarkedCount > 0) {
    return 'saved';
  }
  return 'visited';
}

/**
 * Builds the visible, real-name activity summary (e.g. "Maya and 2 friends
 * saved this"). Exported as a pure function so it's independently testable.
 */
export function buildFriendActivitySummary(friends: FriendActivity[]): string {
  const verb = getActionVerb(friends);
  const [first, ...rest] = friends;

  if (rest.length === 0) {
    return `${first.firstName} ${verb} this`;
  }
  if (rest.length === 1) {
    return `${first.firstName} and 1 friend ${verb} this`;
  }
  return `${first.firstName} and ${rest.length} friends ${verb} this`;
}

/**
 * Display avatars of friends who have interacted with a restaurant, with a
 * real-name text summary. The avatars are decorative (aria-hidden) — the
 * text carries the accessible content.
 */
export const FriendAvatars: React.FC<FriendAvatarsProps> = ({
  friends,
  maxAvatars = 3,
  size = 'xs',
}) => {
  if (!friends || friends.length === 0) {
    return null;
  }

  return (
    <HStack spacing={2} cursor="default">
      <AvatarGroup size={size} max={maxAvatars} spacing="-0.5rem" aria-hidden="true">
        {friends.slice(0, maxAvatars).map((friend) => (
          <Avatar
            key={friend.id}
            name={`${friend.firstName} ${friend.lastName || ''}`}
            src={friend.profilePicture}
            bg="purple.400"
            color="white"
            border="2px solid white"
          />
        ))}
      </AvatarGroup>
      <Text fontSize="xs" color="gray.600" fontWeight="medium">
        {buildFriendActivitySummary(friends)}
      </Text>
    </HStack>
  );
};

export default FriendAvatars;
